import { useEleve } from "../../hooks/fetch_api_hook/useEleve";
import ImportEcoleRattachement from "../../components/VueEcole/ImportEcoleRattachement";
import Loading from "../../components/general/Loading";
import { useState, useEffect } from "react";
import { useUser } from "../../context/UserContext";
import { School, Users, CheckCircle, AlertTriangle, ChevronDown, Upload } from "lucide-react";

function Ecole() {

    const { etablissementData} = useUser();

    const [isEnabled, setIsEnabled] = useState("not-enabled");
    const [showDetails, setShowDetails] = useState(false);
    const [expandedClasses, setExpandedClasses] = useState({});
    const [showImportModal, setShowImportModal] = useState(false);

    useEffect(() => {
      if (etablissementData && etablissementData.id) {
        setIsEnabled("enabled");
      }
    }, [etablissementData]);

    const { data: eleves, isLoading : isLoadingEleves, isError : isErrorEleve } = useEleve("eleves-niveau/", {
    niveau: "6",
    etablissement_id: etablissementData && etablissementData.id,
  },
      isEnabled
    );

  const isGAR = etablissementData?.is_gar === true;

  // Fonction pour obtenir le nom complet d'affichage
  const getEleveDisplayName = (eleve) => {
    if (isGAR && eleve.eleve_gar_nom && eleve.eleve_gar_prenom) {
      return `${eleve.eleve_gar_nom} ${eleve.eleve_gar_prenom}`;
    }
    return `${eleve.nom} ${eleve.prenom}`;
  };

  // Fiche unique (spec GAR §0.1) : une fiche par enfant, GAR ou non — plus de
  // déduplication maths/français ni d'élèves « bloqués » par une matière
  // manquante. Tous les élèves sont importables pour le rattachement écoles.
  const elevesImportables = eleves || [];


  // Filtrer les élèves importables sans école de rattachement (ecole_code et ecole_nom null ou vides)
  const elevesSansEcole = elevesImportables?.filter((eleve) => !eleve.ecole_code && !eleve.ecole_nom) || [];

  // Élèves avec une école de rattachement
  const elevesAvecEcole = elevesImportables?.filter((eleve) => eleve.ecole_code || eleve.ecole_nom) || [];

  // Grouper les élèves par classe
  const groupByClasse = (elevesArray) => {
    return elevesArray.reduce((acc, eleve) => {
      const className = eleve.classe_nom || 'Sans classe';
      if (!acc[className]) {
        acc[className] = [];
      }
      acc[className].push(eleve);
      return acc;
    }, {});
  };

  const elevesSansEcoleGrouped = groupByClasse(elevesSansEcole);


  const toggleClass = (className) => {
    setExpandedClasses(prev => ({
      ...prev,
      [className]: !prev[className]
    }));
  };


  if (!etablissementData) {
    return <Loading loadingText={"Chargement de l'établissement.."} />
  }
  if (isLoadingEleves) {
    return <Loading loadingText={"Chargement des élèves..."} />;
  }

  const isAllComplete = elevesSansEcole.length === 0;

  return (
    <div className="w-full mt-5 flex justify-center">
      <div className="w-fit bg-white rounded-3xl shadow-2xl overflow-hidden">
      {/* Header avec gradient et blobs animés */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-6 xl:px-4 2xl:px-5 3xl:px-6 py-8 xl:py-5 2xl:py-6 3xl:py-8">
        {/* Blobs animés */}
        <svg className="absolute top-0 left-0 w-full h-full opacity-30" viewBox="0 0 200 200">
          <circle cx="20" cy="20" r="40" fill="#67e8f9">
            <animate attributeName="cx" values="20;60;20" dur="8s" repeatCount="indefinite" />
            <animate attributeName="cy" values="20;50;20" dur="6s" repeatCount="indefinite" />
          </circle>
          <circle cx="180" cy="80" r="30" fill="#a5f3fc">
            <animate attributeName="cx" values="180;140;180" dur="7s" repeatCount="indefinite" />
            <animate attributeName="cy" values="80;120;80" dur="9s" repeatCount="indefinite" />
          </circle>
          <circle cx="100" cy="150" r="35" fill="#22d3ee">
            <animate attributeName="cx" values="100;130;100" dur="10s" repeatCount="indefinite" />
            <animate attributeName="cy" values="150;170;150" dur="5s" repeatCount="indefinite" />
          </circle>
        </svg>

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="flex items-center gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
            <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
              <School className="text-white xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8" size={32} />
            </div>
            <div>
              <h1 className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-white">
                Écoles de rattachement
              </h1>
              <p className="text-cyan-100 xl:text-sm 2xl:text-sm 3xl:text-base">
                Gestion des écoles d'origine des élèves de 6ème
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-5xl mx-auto px-6 xl:px-4 2xl:px-5 3xl:px-6 py-8 xl:py-5 2xl:py-6 3xl:py-8 space-y-6 xl:space-y-4 2xl:space-y-5 3xl:space-y-6">

        {/* Carte de statut */}
        <div className={`relative group bg-white/70 backdrop-blur-xl rounded-2xl border shadow-lg overflow-hidden transition-all duration-300 ${
          isAllComplete
            ? 'border-emerald-200/50'
            : 'border-amber-200/50'
        }`}>
          {/* Glow effect */}
          <div className={`absolute -inset-0.5 rounded-2xl blur opacity-0 group-hover:opacity-20 transition duration-300 ${
            isAllComplete
              ? 'bg-gradient-to-r from-emerald-500 to-green-500'
              : 'bg-gradient-to-r from-amber-500 to-orange-500'
          }`} />

          <div className="relative p-6 xl:p-4 2xl:p-5 3xl:p-6">
            <div className="flex items-start gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
              <div className={`p-3 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-xl ${
                isAllComplete ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                {isAllComplete ? (
                  <CheckCircle size={28} className="text-emerald-600 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7" />
                ) : (
                  <AlertTriangle size={28} className="text-amber-600 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7" />
                )}
              </div>

              <div className="flex-1">
                <h2 className={`text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-semibold ${
                  isAllComplete ? 'text-emerald-800' : 'text-amber-800'
                }`}>
                  {isAllComplete
                    ? 'Tous les élèves ont une école de rattachement'
                    : `${elevesSansEcole.length} élève(s) sans école de rattachement`
                  }
                </h2>
                <p className={`mt-1 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm ${
                  isAllComplete ? 'text-emerald-600' : 'text-amber-600'
                }`}>
                  {isAllComplete
                    ? 'Vous pouvez modifier les écoles de secteur en important un fichier.'
                    : 'Veuillez importer un fichier pour compléter les informations manquantes.'
                  }
                </p>

                <div className="flex flex-wrap items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 mt-4 xl:mt-3 2xl:mt-3.5 3xl:mt-4">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className={`inline-flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium rounded-xl transition-all duration-200 ${
                      isAllComplete
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    }`}
                  >
                    <Users size={16} className="xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4" />
                    {showDetails ? 'Masquer les détails' : 'Voir les détails'}
                    <ChevronDown size={16} className={`xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
                  </button>

                  <button
                    onClick={() => setShowImportModal(true)}
                    className="inline-flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/30 hover:scale-[1.02] transition-all duration-200"
                  >
                    <Upload size={16} className="xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4" />
                    Importer les écoles
                  </button>
                </div>
              </div>

              {/* Badge compteur */}
              <div className="hidden sm:block text-right">
                <div className={`inline-flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-xl ${
                  isAllComplete ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                }`}>
                  <Users size={18} className="xl:w-4 xl:h-4 2xl:w-[17px] 2xl:h-[17px] 3xl:w-[18px] 3xl:h-[18px]" />
                  <span className="font-semibold xl:text-sm 2xl:text-sm 3xl:text-base">{elevesImportables?.length || 0}</span>
                  <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">élèves</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section détails des élèves */}
        {showDetails && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/50 shadow-lg p-6 xl:p-4 2xl:p-5 3xl:p-6 space-y-4 xl:space-y-3 2xl:space-y-3.5 3xl:space-y-4">

            {/* Élèves SANS école de rattachement */}
            {elevesSansEcole.length> 0 && <div className="border border-amber-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleClass('section_sans_ecole')}
                className="w-full flex items-center justify-between p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-amber-50 hover:bg-amber-100 transition-colors"
              >
                <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <AlertTriangle size={20} className="text-amber-600 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                  <span className="font-semibold text-amber-800 xl:text-sm 2xl:text-sm 3xl:text-base">
                    Élèves sans école de rattachement ({elevesSansEcole.length})
                  </span>
                </div>
                <ChevronDown
                  size={20}
                  className={`text-amber-600 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 transition-transform ${expandedClasses['section_sans_ecole'] ? 'rotate-180' : ''}`}
                />
              </button>

              {expandedClasses['section_sans_ecole'] && (
                <div className="p-4 xl:p-3 2xl:p-3.5 3xl:p-4 space-y-3 xl:space-y-2 2xl:space-y-2.5 3xl:space-y-3 max-h-[40vh] overflow-auto">
                  {Object.entries(elevesSansEcoleGrouped).map(([className, elevesInClass]) => (
                    <div key={className} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                      <button
                        onClick={() => toggleClass(`sans_${className}`)}
                        className="w-full flex items-center justify-between p-3 xl:p-2 2xl:p-2.5 3xl:p-3 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                          <div className="w-8 h-8 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 rounded-lg flex items-center justify-center font-bold text-white bg-gradient-to-br from-amber-500 to-orange-500 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                            {elevesInClass.length}
                          </div>
                          <span className="font-medium text-slate-800 xl:text-sm 2xl:text-sm 3xl:text-base">Classe {className}</span>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`text-slate-400 xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4 transition-transform ${expandedClasses[`sans_${className}`] ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {expandedClasses[`sans_${className}`] && (
                        <div className="border-t border-slate-200 p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-slate-50/50 space-y-2 xl:space-y-1.5 2xl:space-y-1.5 3xl:space-y-2">
                          {elevesInClass.map((eleve, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-white rounded-lg shadow-sm">
                              <span className="font-medium text-slate-800 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">{getEleveDisplayName(eleve)}</span>
                              <span className="px-2 py-1 xl:px-1.5 xl:py-0.5 2xl:px-1.5 2xl:py-0.5 3xl:px-2 3xl:py-1 rounded-full text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium bg-red-100 text-red-700">
                                Non renseignée
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {elevesSansEcole.length === 0 && (
                    <p className="text-center text-slate-500 py-4">Aucun élève sans école</p>
                  )}
                </div>
              )}
            </div>
}

            {/* Élèves AVEC école de rattachement */}
           {elevesAvecEcole.length > 0  && 
           <div className="border border-emerald-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleClass('section_avec_ecole')}
                className="w-full flex items-center justify-between p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-emerald-50 hover:bg-emerald-100 transition-colors"
              >
                <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <CheckCircle size={20} className="text-emerald-600 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                  <span className="font-semibold text-emerald-800 xl:text-sm 2xl:text-sm 3xl:text-base">
                    Élèves avec école de rattachement ({elevesAvecEcole.length})
                  </span>
                </div>
                <ChevronDown
                  size={20}
                  className={`text-emerald-600 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 transition-transform ${expandedClasses['section_avec_ecole'] ? 'rotate-180' : ''}`}
                />
              </button>

              {expandedClasses['section_avec_ecole'] && (
                <div className="p-4 xl:p-3 2xl:p-3.5 3xl:p-4 space-y-3 xl:space-y-2 2xl:space-y-2.5 3xl:space-y-3 max-h-[40vh] overflow-auto">
                  {Object.entries(groupByClasse(elevesAvecEcole)).map(([className, elevesInClass]) => (
                    <div key={className} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                      <button
                        onClick={() => toggleClass(`avec_${className}`)}
                        className="w-full flex items-center justify-between p-3 xl:p-2 2xl:p-2.5 3xl:p-3 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                          <div className="w-8 h-8 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 rounded-lg flex items-center justify-center font-bold text-white bg-gradient-to-br from-emerald-500 to-green-500 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                            {elevesInClass.length}
                          </div>
                          <span className="font-medium text-slate-800 xl:text-sm 2xl:text-sm 3xl:text-base">Classe {className}</span>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`text-slate-400 xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4 transition-transform ${expandedClasses[`avec_${className}`] ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {expandedClasses[`avec_${className}`] && (
                        <div className="border-t border-slate-200 p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-slate-50/50 space-y-2 xl:space-y-1.5 2xl:space-y-1.5 3xl:space-y-2">
                          {elevesInClass.map((eleve, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-white rounded-lg shadow-sm">
                              <span className="font-medium text-slate-800 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">{getEleveDisplayName(eleve)}</span>
                              <span className="px-2 py-1 xl:px-1.5 xl:py-0.5 2xl:px-1.5 2xl:py-0.5 3xl:px-2 3xl:py-1 rounded-full text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium bg-emerald-100 text-emerald-700">
                                {eleve.ecole_nom}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {elevesAvecEcole.length === 0 && (
                    <p className="text-center text-slate-500 py-4">Aucun élève avec école</p>
                  )}
                </div>
              )}
            </div>
}
          </div>
          
        )}

      </div>

      {/* Modal d'importation */}
      {showImportModal && elevesImportables && elevesImportables.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowImportModal(false)}
          />

          {/* Modal content */}
          <div className="relative max-w-3xl w-full max-h-[90vh] overflow-auto">
            <ImportEcoleRattachement eleves={elevesImportables} onClose={() => setShowImportModal(false)} />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Ecole;